export type Feature = { id: string; type: string };
export type FeatureMap = Record<string, Feature>;

export const waitForPowerpoint = async (): Promise<void> => {
  // Wait briefly for the Office.js script tag to evaluate. The first call
  // can race ahead of the script load — without this, `?.onReady` would
  // silently no-op and the promise would hang.
  const start = Date.now();
  while (typeof window.Office?.onReady !== 'function' && Date.now() - start < 2000) {
    await new Promise((r) => setTimeout(r, 50));
  }
  if (typeof window.Office?.onReady !== 'function') {
    throw new Error('Office.js not loaded');
  }

  // Office.onReady() returns Promise<{host, platform}>. Inside a real Office
  // host this resolves near-instantly; outside one it can stall, so cap with
  // a timeout that callers treat as "not in PowerPoint".
  const info = await Promise.race([
    window.Office.onReady(),
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Office host not detected')), 5000),
    ),
  ]);

  if (!info?.host || !window.Office.context) {
    throw new Error('Not running inside an Office host');
  }
};

export async function isPowerPointDesktop(): Promise<boolean> {
  try {
    await waitForPowerpoint();
    const platform = window?.Office?.context?.diagnostics?.platform;
    return [Office.PlatformType.PC, Office.PlatformType.Mac].includes(platform);
  } catch (error) {
    console.error('Error detecting platform:', error);
    return false;
  }
}

export async function isPowerPointOnline(): Promise<boolean> {
  try {
    await waitForPowerpoint();
    const platform = window.Office.context.diagnostics.platform;
    return [Office.PlatformType.OfficeOnline].includes(platform);
  } catch (error) {
    console.error('Error detecting platform:', error);
    return false;
  }
}

export function isInReadMode(): Promise<boolean> {
  return new Promise(async (resolve) => {
    try {
      await waitForPowerpoint();
      window.Office.context.document.getActiveViewAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(result.value === 'read');
        } else {
          console.error('Failed to get active view:', result.error);
          resolve(false);
        }
      });
    } catch (error) {
      console.error('Exception while checking view mode:', error);
      resolve(false);
    }
  });
}
